/**
 * « Changer d'espace » pill (first row of the content, ADMIN only = roles with ≥ 2 spaces):
 * 36 px #F1F2F6 track, 30 px pills 12.5/700 #666A7B, active white #1B1C31 with a soft shadow.
 * The inactive Création pill carries « {n} en attente » or an activity dot (inbox summary).
 */
import { Link } from "@tanstack/react-router";
import { useApiQuery } from "@/api/hooks";
import { shellApi } from "@/contracts/shell";
import type { StaffRole } from "@/contracts/permissions";
import { cn } from "@/lib/cn";
import { rememberSpace } from "./active";
import { navFor, SPACE_LABEL, type Space } from "./nav";

export function SpaceSwitcher({ spaces, current, role }: { spaces: Space[]; current: Space; role: StaffRole }) {
  const summary = useApiQuery(shellApi.inboxSummary, undefined, { refetchInterval: 30_000, retry: false, staleTime: 20_000 }).data;
  if (spaces.length < 2) return null;
  return (
    <nav aria-label="Changer d'espace" className="mb-5 sm:mb-[22px]">
      {/* < 640 px: 2 × 2 grid (four pills do not fit a phone row); then the drawn 36 px pill track. */}
      <div className="grid grid-cols-2 gap-0.5 rounded-[18px] bg-neutral p-[3px] sm:inline-flex sm:h-9 sm:items-center sm:rounded-full">
        {spaces.map((s) => {
          const active = s === current;
          const first = navFor(s, role)[0]?.items[0];
          // Création: new requests; Compta: onboardings waiting on Melrys — else a dot for recent activity.
          const pending = active ? 0 : s === "creation" ? (summary?.creationNew ?? 0) : s === "compta" ? (summary?.onboardingsToDo ?? 0) : 0;
          const dot = !active && !pending && (s === "creation" ? summary?.creationActivity : s === "compta" ? summary?.comptaActivity : false);
          return (
            <Link
              key={s}
              to={first?.to ?? "/"}
              onClick={() => rememberSpace(s)}
              aria-current={active ? "page" : undefined}
              className={cn(
                "inline-flex h-[30px] min-w-0 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 sm:px-[18px] text-[12.5px] font-bold text-muted transition-colors hover:text-ink",
                active && "bg-white text-ink shadow-[0_1px_3px_rgba(27,28,49,.14)]",
              )}
            >
              {SPACE_LABEL[s]}
              {pending > 0 && (
                <span className="rounded-full bg-danger-bg px-1.5 text-[10.5px] font-bold text-danger" title={`${pending} en attente`}>
                  {pending}
                  <span className="max-[479px]:hidden"> en attente</span>
                </span>
              )}
              {dot && <span className="size-1.5 rounded-full bg-[#818CF8]" aria-label="Activité récente" />}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
